import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Chip,
  IconButton,
  Typography,
  Avatar,
  Stack,
  Alert,
  CircularProgress,
  Card,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { RootState, AppDispatch } from "../store";
import { fetchFirms } from "../store/slices/firmsSlice";
import usersService from "../services/usersService";
import {
  User as UserServiceUser,
  UserCreateInput,
  UserUpdateInput,
} from "../services/usersService";
import { useToast } from "../hooks/useToast";
import { useConfirm } from "../contexts/ConfirmDialogContext";
import { getApiErrorMessage, getApiFieldErrors } from "../utils/apiError";
import PhoneNumberField from "../components/common/PhoneNumberField";

// Extended interface to match API response with nested objects
interface UserWithDetails extends Omit<UserServiceUser, "role" | "position"> {
  role?: {
    id: number;
    name: string;
    description: string;
    created_at: string;
    updated_at: string;
  };
  position?: {
    id: number;
    name: string;
    created_at: string;
    updated_at: string;
  };
  firm?: {
    id: number;
    name: string;
    slug: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    country: string;
    postal_code: string;
    description: string;
    logo_url: string;
    website: string;
    status: string;
    subscription_plan: string;
    subscription_expires_at: string;
    settings: any;
    created_at: string;
    updated_at: string;
    deleted_at: string;
  };
}

interface FormData {
  firm_id: number;
  user_id: string;
  full_name: string;
  email: string;
  role: number;
  phone: string;
  position: number;
  department: string;
  status: "active" | "inactive";
  password: string;
  confirm_password: string;
  organization_id?: number;
}

interface FormErrors {
  full_name?: string;
  email?: string;
  phone?: string;
  password?: string;
  confirm_password?: string;
  role?: string;
}

const initialFormData: FormData = {
  user_id: "",
  full_name: "",
  email: "",
  // No hardcoded role id here (previously `3`) — the real role ids come
  // from the backend and aren't known ahead of time, so a stale/wrong
  // hardcoded default could silently create the user with whatever role
  // that id happens to map to. `0` means "none selected" and is enforced
  // by validateForm below, matching the same reasoning already applied to
  // getRoleOptions() (no fabricated role ids).
  role: 0,
  phone: "",
  department: "",
  position: 1,
  status: "active" as const,
  password: "",
  confirm_password: "",
  firm_id: 0,
};

const Users: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { user: currentUser } = useSelector((state: RootState) => state.auth);
  const { firms } = useSelector((state: RootState) => state.firms);
  const { showSuccess, showError } = useToast();
  const confirm = useConfirm();

  const [rows, setRows] = useState<UserWithDetails[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<UserWithDetails | null>(
    null,
  );
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [roles, setRoles] = useState<any[]>([]);
  const [positions, setPositions] = useState<any[]>([]);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    loadUsers();
    loadRoles();
    loadPositions();
    if (currentUser?.role?.name === "super-admin") {
      dispatch(fetchFirms({ page: 1, limit: 100 }));
    }
  }, [dispatch, currentUser]);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const users = await usersService.getUsers();
      const rowsData = users.data.filter(
        (user: any) => user.role?.name !== "client",
      );
      setRows(rowsData);
    } catch (err: any) {
      const errorMessage = err.message || t('users.failedToLoadUsers');
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      // Like getUsers(), this resolves to the full {status, message, data}
      // envelope — the array is under .data. This was previously missed,
      // so `roles` state held that whole object; the stale `roles.length
      // > 0` check on an object (always undefined, so always falsy) meant
      // the hardcoded mock fallback was silently used 100% of the time.
      // Removing that fallback (below) turned the same bug into a hard
      // "roles.map is not a function" crash — fixing the actual unwrap
      // here instead of restoring the fallback.
      const rolesData = await usersService.getRoles();
      setRoles(rolesData.data || []);
    } catch (err: any) {
      // Previously failed silently (console.error only) and the role
      // dropdown fell back to hardcoded fake roles with fabricated ids —
      // a super-admin picking one of those could assign a role id that
      // doesn't exist in user_roles. Surface the failure instead.
      showError(t('users.failedToLoadRoles', 'Failed to load roles'));
    }
  };

  const loadPositions = async () => {
    try {
      const positionsData = await usersService.getPositions();
      setPositions(positionsData.data || []);
    } catch (err: any) {
      showError(t('users.failedToLoadPositions', 'Failed to load positions'));
    }
  };

  const filteredRows = searchQuery.trim()
    ? rows.filter((row) => {
        const q = searchQuery?.toLowerCase();
        return (
          row.full_name?.toLowerCase().includes(q) ||
          row.email?.toLowerCase().includes(q) ||
          row.role?.name?.toLowerCase().includes(q) ||
          row.position?.name?.toLowerCase().includes(q) ||
          row.firm?.name?.toLowerCase().includes(q)
        );
      })
    : rows;

  const handleOpenDialog = (item?: UserWithDetails) => {
    if (item) {
      setSelectedItem(item);
      setFormData({
        user_id: item.user_id,
        firm_id: item.firm_id,
        full_name: item.full_name,
        email: item.email,
        role: item.role?.id || 0,
        phone: item.phone_no,
        department: item.department,
        status: item.status,
        password: "",
        confirm_password: "",
        position: item.position?.id || 1,
      });
    } else {
      setSelectedItem(null);
      setFormData(initialFormData);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedItem(null);
    setFormData(initialFormData);
    setFormErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Validate required fields
    if (!formData.full_name.trim()) {
      errors.full_name = t('users.full_name');
    }

    if (!formData.email.trim()) {
      errors.email = t('users.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t('users.validEmailAddress');
    }

    if (!formData.role) {
      errors.role = t('users.roleRequired', 'Please select a role');
    }

    // Validate phone number (international format)
    if (
      formData.phone &&
      !/^\+?[1-9]\d{1,14}$/.test(formData.phone.replace(/\s/g, ""))
    ) {
      errors.phone = t('users.validPhoneNumber');
    }

    // Validate passwords for new users or when password is being changed
    if (!selectedItem && !formData.password) {
      errors.password = t('users.passwordRequiredForNewUser');
    } else if (formData.password && formData.password.length < 8) {
      errors.password = t('users.passwordMinLength');
    }

    if (formData.password && formData.password !== formData.confirm_password) {
      errors.confirm_password = t('users.passwordsDoNotMatch');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleFieldChange = (field: keyof FormData, value: string) => {
    setFormData({ ...formData, [field]: value });
    // Clear field error when user starts typing
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    if (!formData.full_name || !formData.email) {
      showError(t('users.nameAndEmailRequired'));
      return;
    }

    if (formData.password && formData.password !== formData.confirm_password) {
      showError(t('users.passwordsDoNotMatch'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (selectedItem) {
        const updateData: UserUpdateInput = {
          user_id: selectedItem.user_id,
          id: selectedItem.id,
          full_name: formData.full_name,
          email: formData.email,
          role: formData.role,
          phone_no: formData.phone,
          department: formData.department,
          status: formData.status,
          position: formData.position,
          firm_id: formData.firm_id,
        };

        if (formData.password) {
          updateData.password = formData.password;
        }

        const result = await usersService.updateUser(updateData);
        const response = result as any;
        if (response["error"]) {
          showError(result.error.message);
          setLoading(false);
          return;
        }
        showSuccess(t('users.userUpdatedSuccess'));
      } else {
        if (!formData.password) {
          showError(t('users.passwordRequiredForNewUser'));
          setLoading(false);
          return;
        }

        const createData: UserCreateInput = {
          full_name: formData.full_name,
          email: formData.email,
          role: formData.role,
          phone_no: formData.phone,
          department: formData.department,
          status: formData.status,
          password: formData.password,
          position: formData.position,
          firm_id: formData.firm_id,
        };

        const result = await usersService.createUser(createData);
        const response = result as any;
        if (response["error"]) {
          showError(response.error.message);
          setLoading(false);
          return;
        }
        showSuccess(t('users.userCreatedSuccess'));
      }
      handleCloseDialog();
      await loadUsers();
    } catch (err: any) {
      // A 422 comes back as `{ errors: { email: ["The email has already
      // been taken."] } }` — surface it on the actual field as well as in
      // the toast, instead of just the generic top-level "Validation
      // failed" message.
      const fieldErrors = getApiFieldErrors(err);
      if (Object.keys(fieldErrors).length > 0) {
        setFormErrors((prev) => ({ ...prev, ...fieldErrors }));
      }
      const errorMessage = getApiErrorMessage(err, t('users.failedToSaveUser'));
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm(t('users.confirmDeleteUser'));
    if (ok) {
      setLoading(true);
      setError(null);
      try {
        await usersService.deleteUser(id);
        showSuccess(t('users.userDeletedSuccess'));
        await loadUsers();
      } catch (err: any) {
        const errorMessage = getApiErrorMessage(err, t('users.failedToDeleteUser'));
        setError(errorMessage);
        showError(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  const getRoleOptions = () => {
    // No hardcoded fallback — showing fabricated role ids here previously
    // meant an admin could pick a "role" that doesn't actually exist in
    // user_roles, silently corrupting the user they were creating/editing.
    // An empty list (with the load failure already toasted) is safer.
    return roles?.map((role: any) => ({
      value: role.id,
      label: role.display_name || role.name,
    }));
  };

  const getPositionOptions = () => {
    return positions.map((position: any) => ({
      value: position.id as number,
      label: position.display_name || position.name,
    }));
  };

  const getNameInitials = (fullName: string) => {
    // Trim whitespace and split the name by one or more spaces
    const nameParts = fullName.trim().split(/\s+/);

    if (nameParts.length === 0) {
      return "";
    }

    // Use map to get the first character of each word, convert to uppercase, and join
    const initials = nameParts
      .map((namePart) => namePart.charAt(0).toUpperCase())
      .join("");

    return initials;
  };

  const getRoleDisplay = (role: string) => {
    const roleMap: Record<string, string> = {
      "super-admin": t('users.roleSuperAdmin'),
      admin: t('users.roleFirmAdmin'),
      lawyer: t('users.roleLawyer'),
      client: t('users.roleClient'),
      staff: t('users.roleStaff'),
    };
    return roleMap[role] || role;
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: t('users.fullName'),
      width: 220,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: "0.8rem",
              bgcolor: "primary.main",
            }}
            src={params.row.avatar_url}
          >
            {params.row.avatar_url
              ? ""
              : `${getNameInitials(params.row.full_name)}`}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {params.row.full_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "role",
      headerName: t('users.role'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={
            params.row.role?.name
              ? getRoleDisplay(params.row.role.name)
              : t('users.noRole')
          }
          size="small"
          color={
            params.row.role?.name === "admin" ||
            params.row.role?.name === "super-admin"
              ? "primary"
              : "default"
          }
        />
      ),
    },
    {
      field: "position",
      headerName: t('common.position'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.row.position?.name || t('users.noPosition')}
        </Typography>
      ),
    },
    {
      field: "firm",
      headerName: t('navigation.firms'),
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <BusinessIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography variant="body2">
            {params.row.firm?.name || t('users.noFirm')}
          </Typography>
        </Box>
      ),
    },
    { field: "phone_no", headerName: t('common.phone'), width: 150 },
    {
      field: "status",
      headerName: t('common.status'),
      width: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.row.status}
          color={params.row.status === "active" ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: t('common.actions'),
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title={t('common.edit')}>
            <IconButton
              size="small"
              onClick={() => handleOpenDialog(params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(params.row.user_id)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">
          {currentUser?.role?.name === "super-admin" ? t('users.allUsers') : t('navigation.users')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {t('users.newUser')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            placeholder={t('users.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 600 }}
          />
        </Box>
      </Card>

      <Card>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={filteredRows}
            columns={columns}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            pageSizeOptions={[10]}
            checkboxSelection
            disableRowSelectionOnClick
            autoHeight
            rowHeight={60}
          />
        )}
      </Card>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {selectedItem ? t('users.editUser') : t('users.newUser')}
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label={t('users.fullName')}
                fullWidth
                value={formData.full_name}
                onChange={(e) => handleFieldChange("full_name", e.target.value)}
                error={!!formErrors.full_name}
                helperText={formErrors.full_name}
              />
            </Box>
            <TextField
              label={t('common.email')}
              fullWidth
              type="email"
              value={formData.email}
              onChange={(e) => handleFieldChange("email", e.target.value)}
              error={!!formErrors.email}
              helperText={formErrors.email}
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControl fullWidth error={!!formErrors.role}>
                <InputLabel>{t('users.role')}</InputLabel>
                <Select
                  value={formData.role || ""}
                  label={t('users.role')}
                  onChange={(e) => {
                    setFormData({ ...formData, role: e.target.value as any });
                    if (formErrors.role) {
                      setFormErrors((prev) => ({ ...prev, role: "" }));
                    }
                  }}
                >
                  {getRoleOptions().map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.role && (
                  <FormHelperText>{formErrors.role}</FormHelperText>
                )}
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>{t('common.position')}</InputLabel>
                <Select
                  value={formData.position}
                  label={t('common.position')}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      position: e.target.value as any,
                    })
                  }
                >
                  {getPositionOptions().map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {currentUser?.role?.name === "super-admin" && firms.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>{t('users.assignToFirm')}</InputLabel>
                <Select
                  value={formData.firm_id || 0}
                  label={t('users.assignToFirm')}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      firm_id: e.target.value as number,
                    })
                  }
                >
                  {firms.map((firm) => (
                    <MenuItem key={firm.id} value={firm.id}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <BusinessIcon fontSize="small" />
                        {firm.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Box sx={{ display: "flex", gap: 2 }}>
              <PhoneNumberField
                value={formData.phone}
                onChange={(value) =>
                  setFormData({ ...formData, phone: value })
                }
                placeholder={t('users.enterPhoneWithCountry')}
                error={formErrors.phone}
                helperText={t('users.enterPhoneWithCountry')}
                required
              />
              <TextField
                select
                label={t('common.status')}
                fullWidth
                value={formData.status}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    status: e.target.value as "active" | "inactive",
                  })
                }
              >
                <MenuItem value="active">{t('users.statusActive')}</MenuItem>
                <MenuItem value="inactive">{t('users.statusInactive')}</MenuItem>
              </TextField>
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label={t('auth.password')}
                type={showPassword ? "text" : "password"}
                fullWidth
                value={formData.password}
                onChange={(e) => handleFieldChange("password", e.target.value)}
                error={!!formErrors.password}
                helperText={
                  formErrors.password ||
                  (!selectedItem
                    ? t('users.requiredForNewUser')
                    : t('users.leaveBlankToKeepPassword'))
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label={t('auth.confirmPassword')}
                type={showConfirmPassword ? "text" : "password"}
                fullWidth
                value={formData.confirm_password}
                onChange={(e) =>
                  handleFieldChange("confirm_password", e.target.value)
                }
                error={!!formErrors.confirm_password}
                helperText={
                  formErrors.confirm_password ||
                  (formData.password &&
                  formData.confirm_password &&
                  formData.password !== formData.confirm_password
                    ? t('users.passwordsDoNotMatch')
                    : "")
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={toggleConfirmPasswordVisibility}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            {selectedItem ? t('common.edit') : t('common.add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
